import { supabase } from 'common/src/supabase';

export type ExternalApiClientStatus = 'active' | 'suspended' | 'revoked';
export type ExternalApiKeyStatus = 'active' | 'revoked';
export type ExternalApiScope = 'market:read' | 'follows:read' | 'follows:write';

export interface ExternalApiGrant {
  id: string;
  client_id?: string;
  species_id: number;
  season: string;
  data_from: string | null;
  data_to: string | null;
  created_at?: string;
}

export interface ExternalApiKey {
  id: string;
  client_id?: string;
  name: string;
  key_id: string;
  scopes: ExternalApiScope[];
  status: ExternalApiKeyStatus;
  expires_at: string | null;
  last_used_at?: string | null;
  created_at: string;
  revoked_at?: string | null;
}

export interface ExternalApiClient {
  id: string;
  name: string;
  exporter_id: number | null;
  status: ExternalApiClientStatus;
  minute_limit: number;
  daily_limit: number;
  created_at: string;
  updated_at: string;
  external_api_client_grants: ExternalApiGrant[];
  external_api_keys: ExternalApiKey[];
}

export interface IssuedExternalApiKey extends ExternalApiKey {
  token: string;
  token_notice: string;
}

interface ApiEnvelope<T> {
  data: T;
  error?: { code?: string; message?: string; details?: unknown } | string;
}

export class ExternalMarketApiAdminError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ExternalMarketApiAdminError';
    this.status = status;
    this.code = code;
  }
}

const functionBaseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/market-data-api-admin/v1`;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new ExternalMarketApiAdminError(
      'Tu sesión expiró. Vuelve a iniciar sesión.',
      401
    );
  }

  const response = await fetch(`${functionBaseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_KEY,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as Partial<
    ApiEnvelope<T>
  >;

  if (!response.ok) {
    const structuredError =
      typeof payload.error === 'object' ? payload.error : undefined;
    const message =
      structuredError?.message ||
      (typeof payload.error === 'string' ? payload.error : undefined) ||
      `La solicitud falló (${response.status}).`;
    throw new ExternalMarketApiAdminError(
      message,
      response.status,
      structuredError?.code
    );
  }

  return payload.data as T;
}

export const externalMarketApiBaseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/market-data-api/v1`;

export function listExternalApiClients(): Promise<ExternalApiClient[]> {
  return request<ExternalApiClient[]>('/clients');
}

export function createExternalApiClient(input: {
  name: string;
  exporter_id: number;
  minute_limit: number;
  daily_limit: number;
}): Promise<ExternalApiClient> {
  return request<ExternalApiClient>('/clients', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateExternalApiClient(
  clientId: string,
  input: Partial<
    Pick<
      ExternalApiClient,
      'name' | 'exporter_id' | 'minute_limit' | 'daily_limit' | 'status'
    >
  >
): Promise<ExternalApiClient> {
  return request<ExternalApiClient>(`/clients/${clientId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function addExternalApiGrant(
  clientId: string,
  input: {
    species_id: number;
    season: string;
    data_from?: string | null;
    data_to?: string | null;
  }
): Promise<ExternalApiGrant> {
  return request<ExternalApiGrant>(`/clients/${clientId}/grants`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function deleteExternalApiGrant(grantId: string): Promise<void> {
  return request<void>(`/grants/${grantId}`, { method: 'DELETE' });
}

export function issueExternalApiKey(
  clientId: string,
  input: {
    name: string;
    scopes: ExternalApiScope[];
    expires_at?: string | null;
  }
): Promise<IssuedExternalApiKey> {
  return request<IssuedExternalApiKey>(`/clients/${clientId}/keys`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function revokeExternalApiKey(keyId: string): Promise<ExternalApiKey> {
  return request<ExternalApiKey>(`/keys/${keyId}/revoke`, {
    method: 'POST',
  });
}
